import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, catchError, of } from 'rxjs';
import { TablesService } from './table.service';
import { ContainerService } from './container.service';
import { OnboardingService } from './onboarding.service';

export interface TutorialStep {
  id: string;
  title: string;
  content: string;
  target: string; // CSS selector for the target element
  position: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  offset?: { x: number; y: number };
  animationTarget?: string; // Optional CSS selector for separate animation target
}

export interface TutorialState {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  steps: TutorialStep[];
}

@Injectable({
  providedIn: 'root'
})
export class TutorialService {
  private tutorialStateSubject = new BehaviorSubject<TutorialState>({
    isActive: false,
    currentStep: 0,
    totalSteps: 0,
    steps: []
  });

  public tutorialState$ = this.tutorialStateSubject.asObservable();

  private emptyStateTutorialSteps: TutorialStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Talonic!',
      content: 'Let\'s take a quick tour to help you get started with creating your first workspace.',
      target: '.welcome-header',
      position: 'bottom-right',
      offset: { x: 0, y: 8 }
    },
    {
      id: 'structures',
      title: 'Structures',
      content: 'Structures define your data logic and spreadsheet structure. We create spreadsheets from any document based on how you define your structures in natural language.',
      target: '#structures-heading',
      position: 'bottom-right',
      offset: { x: 50, y: 8 }
    },
    {
      id: 'ai-generation',
      title: 'Generate with AI',
      content: 'We\'ll automatically find and extract all the data from your documents to fill out your structured table, using our AI technology. The fastest way to get accurate results.',
      target: '.structure-option:first-child .option-icon',
      position: 'bottom-right',
      offset: { x: 40, y: 10 }
    },
    {
      id: 'manual-creation',
      title: 'Manual',
      content: 'If you need to handle complex or unusual documents, you can manually enter or adjust the data for each field.',
      target: '.structure-option:nth-child(2) .option-icon',
      position: 'bottom-right',
      offset: { x: 40, y: 10 }
    },
    {
      id: 'file-upload',
      title: 'Uploading documents',
      content: 'Make sure to upload documents that share a similar layout and data (like invoices from the same supplier). The easiest way to ensure perfect data extraction.',
      target: '.upload-area .small.text-muted:last-child',
      position: 'bottom',
      offset: { x: 0, y: 10 },
      animationTarget: '.upload-area'
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      content: 'Whether it\'s a billing question, a technical glitch, or you just want some expert tips, our support team is ready to assist you quickly and personally.',
      target: '.help-button',
      position: 'top',
      offset: { x: 0, y: -10 }
    }
  ];

  constructor(
    private tablesService: TablesService,
    private containerService: ContainerService,
    private onboardingService: OnboardingService
  ) {}

  startTutorial(): void {
    const newState: TutorialState = {
      isActive: true,
      currentStep: 0,
      totalSteps: this.emptyStateTutorialSteps.length,
      steps: this.emptyStateTutorialSteps
    };
    this.tutorialStateSubject.next(newState);
  }

  nextStep(): void {
    const currentState = this.tutorialStateSubject.value;
    if (currentState.currentStep < currentState.totalSteps - 1) {
      const newState: TutorialState = {
        ...currentState,
        currentStep: currentState.currentStep + 1
      };
      this.tutorialStateSubject.next(newState);
    }
  }

  previousStep(): void {
    const currentState = this.tutorialStateSubject.value;
    if (currentState.currentStep > 0) {
      const newState: TutorialState = {
        ...currentState,
        currentStep: currentState.currentStep - 1
      };
      this.tutorialStateSubject.next(newState);
    }
  }

  closeTutorial(): void {
    const newState: TutorialState = {
      isActive: false,
      currentStep: 0,
      totalSteps: 0,
      steps: []
    };
    this.tutorialStateSubject.next(newState);
  }

  getCurrentStep(): TutorialStep | null {
    const state = this.tutorialStateSubject.value;
    if (state.isActive && state.steps.length > 0) {
      return state.steps[state.currentStep];
    }
    return null;
  }

  getTutorialState(): TutorialState {
    return this.tutorialStateSubject.value;
  }

  // Production mode - only show tutorial for new users without dataspaces
  shouldShowTutorial(hasDataspaces: boolean = false): boolean {
    return !hasDataspaces;
  }

  // Check if user has any dataspaces using container service (same as shell component)
  checkUserDataspaces(): Observable<boolean> {
    return this.containerService.getContainers().pipe(
      map((response: any) => {
        console.log('Tutorial Service - Container service response:', response);
        
        if (response && response.data) {
          // Get tables both from containers and standalone tables
          const containerTables = response.data.dataWithContainerId || [];
          const standaloneTables = response.data.dataWithoutContainerId?.tables || [];
          
          // Flatten all tables from containers
          const allContainerTables = containerTables.flatMap((container: any) => container.tables || []);
          
          // Combine all tables
          const allTables = [...allContainerTables, ...standaloneTables];
          
          console.log('Tutorial Service - Total tables found:', allTables.length);
          
          // Check if user has any dataspaces
          const dataspaces = allTables.filter((table: any) => table.type === 'dataspace');
          console.log('Tutorial Service - Dataspaces found:', dataspaces.length);
          console.log('Tutorial Service - Dataspace objects:', dataspaces);
          
          return dataspaces.length > 0;
        }
        
        console.log('Tutorial Service - No containers data, assuming no dataspaces');
        return false;
      }),
      catchError((error) => {
        console.log('Tutorial Service - Error checking dataspaces:', error);
        return of(false);
      })
    );
  }

  initializeTutorialForNewUsers(): void {
    this.checkUserDataspaces().subscribe(hasDataspaces => {
      if (this.shouldShowTutorial(hasDataspaces)) {
        const checkOnboarding = () => {
          if (this.onboardingService.hasCompletedOnboarding()) {
            setTimeout(() => {
              this.startTutorial();
            }, 1000);
          } else {
            setTimeout(checkOnboarding, 500);
          }
        };
        
        checkOnboarding();
      }
    });
  }
}
